import { c as _c } from "react/compiler-runtime";
import { Avatar, IG, WhatsApp } from '@/components/ui';
import { useI18n } from '@/context/I18nProvider';
import { getProfilePictureUrl, ProfilePictureEntityType } from '@/lib/profile-pictures';
import { ApiClient } from '@/services/api-client';
import { IChannel } from '@/services/types';
import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import Image from 'next/image';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Returns true if the URL is an Instagram/Facebook CDN URL with expiring signatures.
 * These URLs contain temporary query parameters (oh, oe) that expire after hours/days.
 */
function isInstagramCdnUrl(url: string): boolean {
  return url.includes('cdninstagram.com') || url.includes('.fbcdn.net');
}

/**
 * Module-level rate limiter: tracks recently synced lead IDs to avoid spamming
 * the sync-profile API when a profile picture is genuinely unavailable (deleted
 * profile, etc.). Uses sessionStorage for persistence across navigations.
 */
const SYNC_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours
const STORAGE_KEY = 'orsay:profile-sync-cooldowns';
function getSyncCooldowns(): Map<string, number> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return new Map();
    const entries: [string, number][] = JSON.parse(raw);
    const now = Date.now();
    // Prune expired entries on load
    return new Map(entries.filter(([, ts]) => now - ts < SYNC_COOLDOWN_MS));
  } catch {
    return new Map();
  }
}
function setSyncCooldown(leadId: string) {
  const map = getSyncCooldowns();
  map.set(leadId, Date.now());
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...map.entries()]));
  } catch {
    // sessionStorage full or unavailable — degrade to in-memory only
  }
}
function isOnCooldown(leadId: string): boolean {
  const map = getSyncCooldowns();
  const lastSync = map.get(leadId);
  return !!lastSync && Date.now() - lastSync < SYNC_COOLDOWN_MS;
}

/**
 * Global circuit breaker for profile sync requests.
 * After CIRCUIT_BREAKER_THRESHOLD consecutive failures, all syncs are paused
 * for CIRCUIT_BREAKER_COOLDOWN_MS to avoid hammering a broken Instagram API
 * (rate-limited, expired token, outage, etc.).
 */
const CIRCUIT_BREAKER_THRESHOLD = 3;
const CIRCUIT_BREAKER_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes
let consecutiveFailures = 0;
let circuitOpenUntil = 0;
function isCircuitOpen(): boolean {
  if (circuitOpenUntil === 0) return false;
  if (Date.now() >= circuitOpenUntil) {
    // Circuit closed — reset
    circuitOpenUntil = 0;
    consecutiveFailures = 0;
    return false;
  }
  return true;
}
function recordSyncSuccess() {
  consecutiveFailures = 0;
}
function recordSyncFailure(error: unknown) {
  // Only trip the circuit breaker on server errors (5xx) — not on 4xx which
  // are expected for deleted/inaccessible profiles and should not block other syncs.
  const status = error && typeof error === 'object' && 'response' in error ? (error as {
    response?: {
      status?: number;
    };
  }).response?.status : undefined;
  if (status && status < 500) return;
  consecutiveFailures++;
  if (consecutiveFailures >= CIRCUIT_BREAKER_THRESHOLD) {
    circuitOpenUntil = Date.now() + CIRCUIT_BREAKER_COOLDOWN_MS;
    // Drain pending queue — they'd all fail too
    syncQueue.length = 0;
  }
}

/**
 * Global concurrency limiter for profile sync requests.
 * Prevents firing dozens of simultaneous sync-profile API calls when a page
 * loads many leads with stale/missing profile pictures at once.
 */
const MAX_CONCURRENT_SYNCS = 3;
let activeSyncs = 0;
const syncQueue: Array<() => void> = [];
function enqueueSync(fn: () => Promise<unknown>): Promise<void> {
  if (isCircuitOpen()) {
    return Promise.reject(new Error('Circuit breaker open — sync paused'));
  }
  return new Promise<void>((resolve, reject) => {
    const run = () => {
      activeSyncs++;
      fn().then(() => {
        recordSyncSuccess();
        resolve();
      }).catch(err => {
        recordSyncFailure(err);
        reject(err);
      }).finally(() => {
        activeSyncs--;
        const next = syncQueue.shift();
        if (next) next();
      });
    };
    if (activeSyncs < MAX_CONCURRENT_SYNCS) {
      run();
    } else {
      syncQueue.push(run);
    }
  });
}
type ProfileAvatarProps = {
  profilePictureURL?: string;
  displayName: string;
  /** Lead UUID or Instagram user ID (for un-contacted leads). Used as the cache key. */
  leadID?: string;
  /** Connected Instagram account's user ID. When provided, passed to the sync-profile endpoint
   *  so it can authenticate with the Graph API without needing a Lead record in DynamoDB. */
  businessProfileId?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';
  channel?: IChannel;
  /** 'leads' for lead profile pictures (14-day cache), 'users' for Instagram account pictures (permanent). Defaults to 'leads'. */
  entityType?: ProfilePictureEntityType;
};
const ProfileAvatar = t0 => {
  const $ = _c(57);
  const {
    profilePictureURL,
    displayName,
    leadID,
    businessProfileId,
    size: t1,
    className,
    channel,
    entityType: t2
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  const entityType = t2 === undefined ? "leads" : t2;
  const {
    t
  } = useI18n();
  const [shouldShowFallback, setShouldShowFallback] = useState(!profilePictureURL);
  const [useOriginalUrl, setUseOriginalUrl] = useState(false);
  const hasTriedOriginalRef = useRef(false);
  const [syncVersion, setSyncVersion] = useState(0);
  let t3;
  if ($[0] !== channel || $[1] !== profilePictureURL) {
    t3 = channel === IChannel.INSTAGRAM || !!profilePictureURL && isInstagramCdnUrl(profilePictureURL);
    $[0] = channel;
    $[1] = profilePictureURL;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const isInstagram = t3;
  let t4;
  if ($[3] !== businessProfileId || $[4] !== leadID) {
    t4 = async () => {
      if (!leadID) {
        return;
      }
      await enqueueSync(() => ApiClient.integration.synchronizeLeadInstagramProfile(leadID, businessProfileId));
    };
    $[3] = businessProfileId;
    $[4] = leadID;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  let t6;
  if ($[6] !== leadID) {
    t5 = () => {
      if (!leadID) {
        return;
      }
      setSyncCooldown(leadID);
      setSyncVersion(_temp);
      setUseOriginalUrl(false);
      setShouldShowFallback(false);
      hasTriedOriginalRef.current = false;
    };
    t6 = () => {
      if (!leadID) {
        return;
      }
      setSyncCooldown(leadID);
    };
    $[6] = leadID;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t4 || $[10] !== t5 || $[11] !== t6) {
    t7 = {
      mutationFn: t4,
      onSuccess: t5,
      onError: t6
    };
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const syncMutation = useMutation(t7);
  let t8;
  let t9;
  if ($[13] !== profilePictureURL) {
    t8 = () => {
      setShouldShowFallback(!profilePictureURL);
      setUseOriginalUrl(false);
      hasTriedOriginalRef.current = false;
    };
    t9 = [profilePictureURL];
    $[13] = profilePictureURL;
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  useEffect(t8, t9);
  let t10;
  if ($[16] !== businessProfileId || $[17] !== entityType || $[18] !== isInstagram || $[19] !== leadID || $[20] !== profilePictureURL || $[21] !== syncVersion || $[22] !== useOriginalUrl) {
    bb0: {
      if (!profilePictureURL) {
        t10 = undefined;
        break bb0;
      }
      if (isInstagram && !useOriginalUrl && leadID) {
        const url = getProfilePictureUrl(entityType, leadID, profilePictureURL, businessProfileId);
        if (syncVersion > 0) {
          const sep = url?.includes("?") ? "&" : "?";
          t10 = `${url}${sep}v=${syncVersion}`;
          break bb0;
        }
        t10 = url;
        break bb0;
      }
      t10 = profilePictureURL;
    }
    $[16] = businessProfileId;
    $[17] = entityType;
    $[18] = isInstagram;
    $[19] = leadID;
    $[20] = profilePictureURL;
    $[21] = syncVersion;
    $[22] = useOriginalUrl;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const resolvedProfilePictureURL = t10;
  const hashSource = leadID || displayName;
  let t11;
  if ($[24] !== hashSource) {
    t11 = hashSource.split("").reduce(_temp2, 0);
    $[24] = hashSource;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const hash = t11;
  const hue = hash % 360;
  const bgColor = `hsl(${hue}, ${80}%, ${90}%)`;
  const textColor = `hsl(${hue}, ${80}%, ${25}%)`;
  let t12;
  if ($[26] !== bgColor || $[27] !== textColor) {
    t12 = {
      avatarColor: bgColor,
      textColor
    };
    $[26] = bgColor;
    $[27] = textColor;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const {
    avatarColor,
    textColor: textColor_0
  } = t12;
  const ChannelIcon = channel === IChannel.INSTAGRAM ? IG : WhatsApp;
  const t13 = size === "sm";
  const t14 = size === "md";
  const t15 = size === "lg";
  const t16 = size === "xl";
  const t17 = size === "2xl";
  const t18 = size === "3xl";
  const t19 = size === "4xl";
  let t20;
  if ($[29] !== className || $[30] !== t13 || $[31] !== t14 || $[32] !== t15 || $[33] !== t16 || $[34] !== t17 || $[35] !== t18 || $[36] !== t19) {
    t20 = clsx("overflow-visible", {
      "size-4": t13,
      "size-6": t14,
      "size-8": t15,
      "size-10": t16,
      "size-12": t17,
      "size-14": t18,
      "size-16": t19
    }, className);
    $[29] = className;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
    $[34] = t17;
    $[35] = t18;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== avatarColor || $[39] !== displayName || $[40] !== entityType || $[41] !== isInstagram || $[42] !== leadID || $[43] !== resolvedProfilePictureURL || $[44] !== shouldShowFallback || $[45] !== size || $[46] !== syncMutation || $[47] !== t || $[48] !== textColor_0) {
    t21 = shouldShowFallback ? <div className={clsx("flex h-full w-full items-center justify-center font-medium overflow-hidden rounded-full", {
      "text-[10px]": size === "sm",
      "text-xs": size === "md",
      "text-sm": size === "lg",
      "text-base": size === "xl",
      "text-lg": size === "2xl",
      "text-xl": size === "3xl",
      "text-2xl": size === "4xl"
    })} style={{
      background: avatarColor,
      color: textColor_0
    }}>{displayName?.charAt(0).toUpperCase() ?? "U"}</div> : <Image className="flex h-full w-full items-center justify-center font-medium overflow-hidden rounded-full object-cover" src={resolvedProfilePictureURL} alt={t("common.profilePhoto")} fill={true} unoptimized={true} onError={() => {
      if (isInstagram && !hasTriedOriginalRef.current) {
        hasTriedOriginalRef.current = true;
        setUseOriginalUrl(true);
        return;
      }
      setShouldShowFallback(true);
      if (isInstagram && entityType === "leads" && leadID && !syncMutation.isPending && !isCircuitOpen()) {
        if (!isOnCooldown(leadID)) {
          syncMutation.mutate();
        }
      }
    }} />;
    $[38] = avatarColor;
    $[39] = displayName;
    $[40] = entityType;
    $[41] = isInstagram;
    $[42] = leadID;
    $[43] = resolvedProfilePictureURL;
    $[44] = shouldShowFallback;
    $[45] = size;
    $[46] = syncMutation;
    $[47] = t;
    $[48] = textColor_0;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== ChannelIcon || $[51] !== channel) {
    t22 = channel && <ChannelIcon className="absolute bottom-[-19%] right-[-19%] h-3/5 w-3/5" />;
    $[50] = ChannelIcon;
    $[51] = channel;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== t20 || $[54] !== t21 || $[55] !== t22) {
    t23 = <Avatar className={t20}>{t21}{t22}</Avatar>;
    $[53] = t20;
    $[54] = t21;
    $[55] = t22;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  return t23;
};
export { ProfileAvatar };
function _temp(v) {
  return v + 1;
}
function _temp2(acc, char) {
  return acc * 31 + char.charCodeAt(0) & 4294967295;
}